// Protected React host for the supplied, unchanged SCOR diagnostic document.
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, LogIn, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePoPAuth } from '../context/PoPAuthContext';
import { popLogin } from '../lib/popApi';
import { scorDocument } from '../utils/scorHost';

export default function DiagnosticPage({ getToken, onSaved }) {
  const { user, loading } = usePoPAuth();
  const frameRef = useRef(null);
  const [documentHtml, setDocumentHtml] = useState('');
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    setDocumentHtml('');
    if (!user?.user_id) return;
    const controller = new AbortController();
    fetch('/scor-diagnostic.html', { signal: controller.signal })
      .then(response => { if (!response.ok) throw new Error('Could not load SCOR Diagnostic.'); return response.text(); })
      .then(html => { setError(''); setDocumentHtml(scorDocument(html, user.user_id)); })
      .catch(err => { if (err.name !== 'AbortError') setError(err.message); });
    return () => controller.abort();
  }, [user?.user_id, retry]);

  useEffect(() => {
    const onMessage = event => {
      if (event.origin !== window.location.origin || event.source !== frameRef.current?.contentWindow) return;
      if (event.data?.type === 'scor:saved' && onSaved) onSaved(event.data);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onSaved]);

  const sendAuth = () => {
    // Existing auth uses HttpOnly cookies. No token is copied into storage or a URL.
    const token = getToken?.();
    if (token) frameRef.current?.contentWindow?.postMessage({ type: 'scor:auth', token }, window.location.origin);
  };

  return <div className="min-h-screen bg-white" data-testid="scor-diagnostic-page">
    <header className="min-h-16 px-4 sm:px-6 flex items-center justify-between gap-3 border-b border-gray-200">
      <Link to="/" data-testid="scor-back-home" className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-800"><ArrowLeft className="w-4 h-4" />SAMANVAI</Link>
      <h1 data-testid="scor-page-title" className="text-base sm:text-lg font-semibold text-gray-900">SCOR Diagnostic</h1>
    </header>
    {loading ? <div data-testid="scor-auth-loading" role="status" className="p-10 flex justify-center"><Loader2 className="animate-spin" /></div>
      : !user ? <main className="max-w-lg mx-auto px-6 py-20">
        <ShieldCheck className="w-10 h-10 text-emerald-700 mb-5" />
        <h2 data-testid="scor-signin-heading" className="text-lg font-semibold mb-4">Sign in to your private SCOR workspace</h2>
        <Button data-testid="scor-signin-button" onClick={() => popLogin('/diagnostic/auth/callback')} className="bg-emerald-700 hover:bg-emerald-800"><LogIn className="w-4 h-4 mr-2" />Sign in with Google</Button>
      </main> : error ? <div className="p-8" role="alert" data-testid="scor-load-error">{error}<Button data-testid="scor-load-retry" onClick={() => setRetry(value => value + 1)} className="ml-3">Try again</Button></div>
        : documentHtml ? <iframe key={user.user_id} data-testid="scor-diagnostic-frame" ref={frameRef} onLoad={sendAuth}
          title="SCOR MTO Diagnostic" srcDoc={documentHtml}
          sandbox="allow-scripts allow-same-origin allow-downloads allow-modals allow-popups"
          style={{ width: '100%', height: 'calc(100dvh - 64px)', minHeight: 480, border: 0, display: 'block' }} allow="clipboard-write" />
          : <div data-testid="scor-document-loading" role="status" className="p-10 flex justify-center"><Loader2 className="animate-spin" /></div>}
  </div>;
}